import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { FIELD_REQUIRED, LOADING } from "../../../../i18n/keysTranslations";
import { Controller, useFormContext } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetCatalogsAdsCampaigns } from "../../../../actions/adsCampaigns";
import { DELAY_DEBOUNCE, META } from "../../../../utils/constants";
import { useDebounced } from "../../../../hooks/useDebounce";
import _ from "lodash";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import FieldCaption from "../../../Form/FieldCaption";

const SelectCatalogMeta = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  disableClearable = false,
  sxTextField = {},
}) => {
  const { watch } = useFormContext();
  const catalogID = watch(name);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const onGetCatalogsAdsCampaigns = async () => {
    setLoading(true);
    const response = await dispatch(
      startGetCatalogsAdsCampaigns({
        integrationID,
        adAccountID,
        includedID: catalogID,
        from: "catalog",
        platform: META,
      })
    );
    if (response.ok) {
      const data = response.data;
      if (data?.length > 0) {
        setOptions(data);
      }
    }
    setLoading(false);
  };

  const onSearchCatalogsAdsCampaigns = useDebounced(
    async (search) => {
      setLoading(true);
      const response = await dispatch(
        startGetCatalogsAdsCampaigns({
          integrationID,
          adAccountID,
          name: search,
          from: "catalog",
          platform: META,
        })
      );
      if (response.ok) {
        const data = response.data;
        if (data?.length > 0) {
          setOptions((prev) => _.uniqBy([...prev, ...data], "id"));
        }
      }
      setLoading(false);
    },
    [DELAY_DEBOUNCE]
  );

  useEffect(() => {
    onGetCatalogsAdsCampaigns();
    // eslint-disable-next-line
  }, [integrationID, adAccountID]);

  return (
    <>
      <Controller
        defaultValue={""}
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Autocomplete
            {...field}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            disableClearable={disableClearable}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            loading={loading}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onInputChange={(event, newInputValue) => {
              if (!newInputValue || !event) return;
              onSearchCatalogsAdsCampaigns(newInputValue);
            }}
            onChange={(_, value) => {
              if (value?.id) {
                field.onChange(value.id);
                onChangeAction(value);
              } else {
                field.onChange(null);
                onChangeAction(null);
              }
            }}
            isOptionEqualToValue={(option, value) => option?.id === value}
            getOptionLabel={(option) => {
              if (option == null || option === "") return "";
              const optionValue = options.find((opt) => opt.id === option);
              if (optionValue) {
                return `${optionValue?.name} - ${optionValue?.id}`;
              }
              if (typeof option === "string") {
                return option;
              }
              return option?.name ? `${option.name} - ${option.id}` : "";
            }}
            options={options}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={option.value}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="body2">
                    {option.name} - {option.id}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                id={name}
                {...params}
                label={label}
                variant={variant}
                hiddenLabel={!label}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
      />
      <FieldCaption error={error} helperText={helperText} />
    </>
  );
};

export default SelectCatalogMeta;
